/**
 * The faces in a picture (asked for 2026-10-11: "automatically assign the
 * pictures to the zones, and focus on the faces"), found on this device - the
 * pictures still never leave it.
 *
 * Google's MediaPipe face detector (BlazeFace, short range: faces filling a
 * fair part of the photo, as family photos mostly have them). Its WebAssembly
 * (about 12 MB) and model (230 KB) come from this site, not a CDN, and only
 * the first time faces are looked for. A browser without WebAssembly SIMD
 * (older than about 2021) finds none, and pictures are simply centred.
 */
import type { FaceDetector } from '@mediapipe/tasks-vision';
import wasmLoaderPath from '@mediapipe/tasks-vision/vision_wasm_internal.js?url';
import wasmBinaryPath from '@mediapipe/tasks-vision/vision_wasm_internal.wasm?url';
import modelAssetPath from './blaze_face_short_range.tflite?url';

/** A face's box, as fractions of the picture's width and height (0..1). */
export interface FaceBox { x: number; y: number; w: number; h: number; score: number }

let detector: Promise<FaceDetector | null> | null = null;

function ready(): Promise<FaceDetector | null> {
  detector ??= (async () => {
    try {
      const { FaceDetector } = await import('@mediapipe/tasks-vision');
      return await FaceDetector.createFromOptions({ wasmLoaderPath, wasmBinaryPath }, {
        baseOptions: { modelAssetPath, delegate: 'CPU' },
        runningMode: 'IMAGE',
        minDetectionConfidence: 0.55,
      });
    } catch {
      return null;
    }
  })();
  return detector;
}

/** Each picture is looked at once a visit: picture id -> its faces. */
const seen = new Map<string, FaceBox[]>();

/** The faces in a picture (its `href` is any copy of it - the preview will do), biggest first. */
export async function facesIn(id: string, href: string): Promise<FaceBox[]> {
  const known = seen.get(id);
  if (known) return known;
  const d = await ready();
  if (!d) return [];
  // a bitmap, not an <img>: a browser may hold an image's decoding back while
  // the page is out of sight, and the search would wait for it
  const img = await createImageBitmap(await (await fetch(href)).blob());
  const W = img.width, H = img.height;
  let found;
  try {
    found = d.detect(img);
  } finally {
    img.close();
  }
  const faces = found.detections.flatMap((x) => {
    const b = x.boundingBox;
    if (!b) return [];
    return [{ x: b.originX / W, y: b.originY / H, w: b.width / W, h: b.height / H, score: x.categories[0]?.score ?? 0 }];
  }).sort((a, b) => b.w * b.h - a.w * a.h);
  seen.set(id, faces);
  return faces;
}
